// shared/context/contingencia-efatura-context.js

import { createContext, useState, useEffect, useCallback } from "react";

export const ContingenciaEfaturaContext = createContext({
  modoContingenciaAtivo: false,
  reasonTypeCode: 5,
  reasonDescription: "",
  dataAtivacao: null,
  ativarModoContingencia: () => {},
  desativarModoContingencia: () => {},
  getDadosContingencia: () => {},
});

const STORAGE_KEY = "odontoht_contingencia_efatura";
const TEMPO_EXPIRACAO_MS = 4 * 60 * 60 * 1000; // 4 horas em milissegundos

export const ContingenciaEfaturaProvider = ({ children }) => {
  const [modoContingenciaAtivo, setModoContingenciaAtivo] = useState(false);
  const [reasonTypeCode, setReasonTypeCode] = useState(5);
  const [reasonDescription, setReasonDescription] = useState("");
  const [dataAtivacao, setDataAtivacao] = useState(null);

  // Carregar dados do localStorage ao inicializar
  useEffect(() => {
    try {
      const dadosArmazenados = localStorage.getItem(STORAGE_KEY);

      if (dadosArmazenados) {
        const dados = JSON.parse(dadosArmazenados);
        const agora = new Date().getTime();
        const tempoDecorrido = agora - new Date(dados.dataAtivacao).getTime();

        // Verifica se ainda está dentro do prazo de 4 horas
        if (tempoDecorrido < TEMPO_EXPIRACAO_MS) {
          setModoContingenciaAtivo(dados.modoContingenciaAtivo);
          setReasonTypeCode(dados.reasonTypeCode);
          setReasonDescription(dados.reasonDescription || "");
          setDataAtivacao(dados.dataAtivacao);
        } else {
          // Expirou - limpar localStorage
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar dados de contingência:", error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Verificar expiração periodicamente (a cada minuto)
  useEffect(() => {
    if (!modoContingenciaAtivo) return;

    const intervalId = setInterval(() => {
      const agora = new Date().getTime();
      const tempoDecorrido = agora - new Date(dataAtivacao).getTime();

      if (tempoDecorrido >= TEMPO_EXPIRACAO_MS) {
        desativarModoContingencia();
      }
    }, 60000); // Verifica a cada 1 minuto

    return () => clearInterval(intervalId);
  }, [modoContingenciaAtivo, dataAtivacao]);

  // Salvar no localStorage sempre que houver mudança
  useEffect(() => {
    if (modoContingenciaAtivo) {
      const dados = {
        modoContingenciaAtivo,
        reasonTypeCode,
        reasonDescription,
        dataAtivacao,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dados));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [modoContingenciaAtivo, reasonTypeCode, reasonDescription, dataAtivacao]);

  const ativarModoContingencia = useCallback((codigo = 5, descricao = "") => {
    const agora = new Date().toISOString();

    setModoContingenciaAtivo(true);
    setReasonTypeCode(codigo);
    setReasonDescription(descricao);
    setDataAtivacao(agora);
  }, []);

  const desativarModoContingencia = useCallback(() => {
    setModoContingenciaAtivo(false);
    setReasonTypeCode(5);
    setReasonDescription("");
    setDataAtivacao(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getDadosContingencia = useCallback(() => {
    if (!modoContingenciaAtivo) return null;

    return {
      emitirEmContingencia: true,
      ReasonTypeCode: reasonTypeCode,
      ...(reasonTypeCode === 0 && { ReasonDescription: reasonDescription }),
    };
  }, [modoContingenciaAtivo, reasonTypeCode, reasonDescription]);

  const getTempoRestante = useCallback(() => {
    if (!modoContingenciaAtivo || !dataAtivacao) return null;

    const agora = new Date().getTime();
    const tempoDecorrido = agora - new Date(dataAtivacao).getTime();
    const tempoRestante = TEMPO_EXPIRACAO_MS - tempoDecorrido;

    if (tempoRestante <= 0) return 0;

    const horas = Math.floor(tempoRestante / (60 * 60 * 1000));
    const minutos = Math.floor(
      (tempoRestante % (60 * 60 * 1000)) / (60 * 1000)
    );

    return {
      total: tempoRestante,
      horas,
      minutos,
      texto: `${horas}h ${minutos}m`,
    };
  }, [modoContingenciaAtivo, dataAtivacao]);

  return (
    <ContingenciaEfaturaContext.Provider
      value={{
        modoContingenciaAtivo,
        reasonTypeCode,
        reasonDescription,
        dataAtivacao,
        ativarModoContingencia,
        desativarModoContingencia,
        getDadosContingencia,
        getTempoRestante,
      }}
    >
      {children}
    </ContingenciaEfaturaContext.Provider>
  );
};
